import { useEffect, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { fetchGoalsRollup } from '../../api/endpointsBackend';
import { monthLong } from '../../utils/intlFormatters';
import { daysLeftInMonth, formatAmount } from '../../utils/goals';
import Card from '../shared/Card';
import Figure from '../shared/Figure';
import GoalMonthRing from './GoalMonthRing';

/**
 * This month across active goals: what was saved, and what is still needed
 * before the month ends. One currency, converted at each movement's own rate.
 */
export default function GoalsRollupCard({ currency, refreshKey }) {
  const { t } = useTranslation('goals');
  const [rollup, setRollup] = useState(null);

  useEffect(() => {
    if (!currency) return undefined;
    let active = true;
    fetchGoalsRollup(currency)
      .then((data) => { if (active) setRollup(data); })
      .catch(() => { if (active) setRollup(null); });
    return () => { active = false; };
  }, [currency, refreshKey]);

  if (!rollup || rollup.num_goals === 0) return null;

  const month = monthLong(new Date().getMonth());
  const days = daysLeftInMonth();

  return (
    <Card px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }} mb={8}>
      <Flex align="center" justify="space-between" gap={5} wrap="wrap">
        <Box minW={0}>
          <Text textStyle="microLabel" mb={2}>{t('rollup.savedIn', { month })}</Text>
          <Figure code={rollup.currency} amount={formatAmount(rollup.saved)} display="block" />
          <Text fontSize="xs" color="redesign.text.muted" mt={2}>
            {t(rollup.num_goals === 1 ? 'rollup.activeGoalsOne' : 'rollup.activeGoalsOther', { count: rollup.num_goals })}
            {' · '}
            {t('rollup.neededThisMonth', { amount: formatAmount(rollup.required) })}
          </Text>
          {/* Figures that left something out say so, rather than reading exact. */}
          {rollup.num_unconvertible > 0 && (
            <Text fontSize="xs" color="redesign.text.muted" mt={1}>
              {t('rollup.unconvertible', { count: rollup.num_unconvertible })}
            </Text>
          )}
        </Box>
        <Flex align="center" gap={5} flexShrink={0}>
        <Box textAlign={{ base: 'left', sm: 'right' }} flexShrink={0}>
          <Text textStyle="microLabel" mb={2}>{t('rollup.toGo')}</Text>
          <Figure amount={formatAmount(rollup.to_go)} display="block" />
          <Text fontSize="xs" color="redesign.text.muted" mt={2}>
            {t(days === 1 ? 'rollup.daysLeftOne' : 'rollup.daysLeftOther', { count: days })}
          </Text>
        </Box>
        {rollup.required > 0 && (
          <Box display={{ base: 'none', md: 'block' }}>
            <GoalMonthRing pct={(rollup.saved / rollup.required) * 100} />
          </Box>
        )}
        </Flex>
      </Flex>
    </Card>
  );
}
